import { create, CLIENT_ERROR, SERVER_ERROR, TIMEOUT_ERROR, NETWORK_ERROR } from 'apisauce';
import type { ApisauceInstance, ApiResponse } from 'apisauce';
import { DOMAIN, API_BASE_URL, USE_FEDERATED_AUTH, DELOGUE_API_KEY } from 'config';

import { object, string, array } from 'yup';
import { ErrorType } from '../types';
import { logoutFn } from 'features/auth/utils/token';
import type { CombinedTypes } from 'features/notifications/types';
import type { PaginateResponse } from 'types';
import { t } from '@lingui/macro';

// The setDelogueApi token is set after a successful login with Auth0
// Needed for API instances, to enable setting authorization header
let delogueApiToken: string | null = null;

export const setDelogueApiToken = (token: string | null) => {
  delogueApiToken = token;
};

export const isDelogueApiEnabled = () => delogueApiToken !== null;

/** Read side of the Auth0 bearer for callers replicating the instances' auth outside apisauce (DL-61815). */
export const getDelogueApiToken = () => delogueApiToken;

/**
 * Shared 401 handling for the legacy API — the apisauce `authMonitor` and the DL-61815
 * native binary transport both delegate here so the DL-61312 loop breaker stays
 * single-sourced: a 401 that already forced a logout + reload moments ago must surface
 * inline instead of bouncing through logout/auto-login forever. Returns true when a
 * logout+reload was initiated.
 */
export const handleLegacy401 = async (): Promise<boolean> => {
  if (!shouldLogoutOn401()) return false;
  await logoutFn();
  // DL-61835 (R5/X3): in federated mode logoutFn() already navigates top-level to Auth0's
  // /v2/logout to finish killing the shared SSO session — calling replace('/') here too
  // would race that navigation and cancel it. Non-federated logoutFn() never navigates, so
  // the reset-to-'/' below is still what completes the logout there.
  if (!USE_FEDERATED_AUTH) {
    // use native navigate to reset all session state
    window.location.replace('/');
  }
  return true;
};

// A 401 from the legacy API forces a logout + full reload. Remember when that last
// happened (sessionStorage survives the reload and the Auth0 round-trip): if the freshly
// re-authenticated session *immediately* 401s again, logging out a second time would spin
// the /style -> / -> /style redirect loop forever (DL-61312 — the legacy backend threw
// before the request's user resolved, so every page load 401'd, /betalogin/logout never
// ended the Auth0 session, and each reload silently re-authenticated).
export const LAST_401_LOGOUT_KEY = 'legacy-api-401-logout-at';
const LOGOUT_401_LOOP_WINDOW_MS = 60_000;

export const shouldLogoutOn401 = (now: number = Date.now()): boolean => {
  try {
    const last = Number(sessionStorage.getItem(LAST_401_LOGOUT_KEY)) || 0;
    if (now - last < LOGOUT_401_LOOP_WINDOW_MS) return false;
    sessionStorage.setItem(LAST_401_LOGOUT_KEY, String(now));
    return true;
  } catch {
    // sessionStorage unavailable — fall back to the pre-existing always-logout behavior
    return true;
  }
};

/**
 * Legacy API instance - uses DOMAIN (e.g., http://localhost:1105)
 * This is the main API for most application endpoints.
 */
export const api = (() => {
  const instance = create({
    baseURL: DOMAIN,
    headers: {
      Accept: 'application/json',
      'Cache-Control': 'no-cache',
      'Access-Control-Allow-Origin': '*',
    },
    timeout: 180000,
    withCredentials: !USE_FEDERATED_AUTH, //For Auth0 JWT based auth, we don't need to send cookies
  });

  instance.addRequestTransform(request => {
    if (delogueApiToken) {
      request.headers = request.headers || {};
      request.headers.Authorization = `Bearer ${delogueApiToken}`;
    }
  });

  const authMonitor = async (response: ApiResponse<unknown>) => {
    // log user out if unauthorized
    if (response.status === 401) {
      // A 401 from the legacy API forces a logout + full reload, with a loop-breaker
      // (DL-61312) and federated-aware navigation (DL-61835) — both live in the shared
      // handleLegacy401() helper so the delogueApi monitor (below) and nativePdfTransport
      // share it.
      await handleLegacy401();
    }
  };
  instance.addMonitor(authMonitor);

  return instance;
})();

/**
 * DL-60478: the 401 handling for the new API, mirroring the legacy instance's `authMonitor`.
 * Without it a Nexos 401 neither logged the user out nor redirected — the session was dead
 * but every page kept rendering as though it were live (`.claude/rules/error-handling.md`
 * §2.5 named this gap). Delegates to the same `handleLegacy401()` so the DL-61312
 * loop-breaker and the DL-61835 federated navigation stay single-sourced.
 *
 * Gated on `delogueApiToken` deliberately. A 401 with **no** bearer attached is not a lost
 * session — it is a call made without Auth0 ever issuing a token, which is the steady state
 * in every environment where `VITE_USE_FEDERATED_AUTH` is false and nothing sets the token
 * at all. Logging out there would turn "this deployment cannot reach the new API" into a
 * spurious logout for every user on it. Only a token the API actively *rejects* means the
 * session is gone.
 */
export const delogueApi401Monitor = async (response: ApiResponse<unknown>): Promise<void> => {
  if (response.status === 401 && delogueApiToken !== null) {
    await handleLegacy401();
  }
};

/**
 * New Delogue.Api instance - uses API_BASE_URL (e.g., https://api.delogue.com)
 * This is the API to use going forward for all API requests.
 */
export const delogueApi = (() => {
  const instance = create({
    baseURL: API_BASE_URL,
    headers: {
      Accept: 'application/json',
      'Cache-Control': 'no-cache',
      'Access-Control-Allow-Origin': '*',
    },
    timeout: 180000,
  });

  instance.addRequestTransform(request => {
    request.headers = request.headers || {};
    if (delogueApiToken) {
      request.headers.Authorization = `Bearer ${delogueApiToken}`;
    }
    // LOCAL DEV (federated off): authenticate to Delogue.API with a user API key via its
    // ApiKey scheme — the Nexos scheme selector routes any request carrying X-Auth-Token to
    // the api-key handler, so no Auth0 bearer is needed. Unset in deployed builds.
    if (DELOGUE_API_KEY) {
      request.headers['X-Auth-Token'] = DELOGUE_API_KEY;
    }
  });

  instance.addMonitor(delogueApi401Monitor);

  return instance;
})();

export async function handleRequest<T>(
  promise: Promise<ApiResponse<T, T>>,
  notify?: (notification: CombinedTypes) => void
) {
  const res = await promise;
  if (!res.ok) {
    const errorMessage = await validateError(res as ApiResponse<ErrorType, ErrorType>);
    const message = errorMessage?.code || errorMessage?.msg || '';
    if (notify) {
      const isCode = Boolean(errorMessage?.code);
      notify({ message, id: 'api-error', autoClose: false, isCode });
    }
    throw new Error(message);
  }
  const inValid = validateResponse(res);
  if (inValid || !res.data) {
    throw new Error(inValid);
  }
  return res.data;
}

async function adapterBase(
  promise: Promise<ApiResponse<unknown, unknown>>,
  notify?: (notification: CombinedTypes) => void
) {
  const res = await promise;
  if (!res.ok) {
    const errorMessage = await validateError(res as ApiResponse<ErrorType, ErrorType>);

    const header = errorMessage?.code || errorMessage?.msg || '';
    if (notify) {
      notify({
        header,
        id: 'api-error',
        autoClose: false,
        isCode: Boolean(errorMessage?.code),
        messageList: errorMessage?.messageList,
      });
    }
    throw new Error(header);
  }
  const inValid = validateResponse(res);
  if (inValid || !res.data) {
    throw new Error(inValid);
  }
  return res;
}

export async function adapterPaged<ApiType, TransformedType>(
  promise: Promise<ApiResponse<unknown, unknown>>,
  transform?: (data: ApiType[]) => TransformedType[],
  notify?: (notification: CombinedTypes) => void
): Promise<PaginateResponse<TransformedType> | PaginateResponse<ApiType>> {
  const res = await adapterBase(promise, notify);
  const transformed = res.data as PaginateResponse<ApiType>;
  if (transform) {
    return { ...transformed, data: transform(transformed.data) };
  }
  return transformed;
}

export async function adapter<ApiType, TransformedType>(
  promise: Promise<ApiResponse<unknown, unknown>>,
  transform?: (data: ApiType) => TransformedType,
  notify?: (notification: CombinedTypes) => void
): Promise<TransformedType | ApiType> {
  const res = await adapterBase(promise, notify);
  const transformed = res.data as ApiType;
  if (transform) {
    return transform(transformed);
  }
  return transformed;
}

function validateResponse<T>(res: ApiResponse<T, T>) {
  const contentType = res?.headers?.['content-type'];
  if (res?.config?.method === 'get') {
    if (!contentType?.includes('application/json')) {
      return `Invalid response type (${contentType}), expected application/json.`;
    }
    if (!res?.data) {
      return 'No data in response.';
    }
  }
  return '';
}

const validateError = async (res: ApiResponse<ErrorType, ErrorType>) => {
  if (res.problem === CLIENT_ERROR) {
    const errorMessage = await validateClientError(res as ApiResponse<ErrorType, ErrorType>);
    return errorMessage;
  }
  if (res.problem === SERVER_ERROR) {
    const errorMessage = await validateClientError(res as ApiResponse<ErrorType, ErrorType>);
    return errorMessage;
  }
  if (res.problem === TIMEOUT_ERROR) {
    return {
      msg: `${t`Server didn't respond in time`} (${
        res.config?.timeout ? res.config.timeout / 1000 : 0
      } ${t`seconds`}).`,
    };
  }
  if (res.problem === NETWORK_ERROR) {
    return { msg: t`Internet connection error.` };
  }
  return { msg: `${res.problem}.` };
};

const errorSchema = object({
  code: string().required(),
  status: string().required(),
  data: object().nullable().optional(),
  error: object({
    details: array().of(object({ type: string().required(), value: string().required() })),
  })
    .nullable()
    .optional(),
});

const validateClientError = async (res: ApiResponse<ErrorType, ErrorType>) => {
  const data = res?.data;

  try {
    await errorSchema.validate(data, { abortEarly: false });
  } catch (error) {
    const messages: string[] = [];
    (error as { inner: Error[] }).inner.forEach((innerError: Error) => {
      messages.push(innerError.message);
    });
    return { msg: `Invalid error response. ${messages.join(', ')}` };
  }
  return {
    code: data?.code,
    messageList: data?.error?.details?.map(item => ({
      code: item?.code ?? item?.type,
      msg: item?.value,
    })),
  };
};

/**
 * Converts a camelCase string to PascalCase.
 * @param str - The camelCase string.
 * @returns The PascalCase string.
 */
const camelToPascal = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Recursively converts the keys of an object from camelCase to PascalCase.
 * @param obj - The object with camelCase keys.
 * @returns The object with PascalCase keys.
 */
export const objCamelToPascal = (
  obj: Record<string, unknown> | Record<string, unknown>[]
): Record<string, unknown> | Record<string, unknown>[] => {
  if (Array.isArray(obj)) {
    return obj.map(item => objCamelToPascal(item) as Record<string, unknown>);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce(
      (acc, key) => {
        const pascalKey = camelToPascal(key);
        acc[pascalKey] = objCamelToPascal(obj[key] as Record<string, unknown>);
        return acc;
      },
      {} as Record<string, unknown>
    );
  }
  return obj;
};
